import { Koi } from '@ksyos/koi';
import type { TypedReadSchema } from '@common/formEngine/typedReadSchema.types';
import type { TypedSchema } from '@common/validation/utils/typedSchemaType';
import {
    bloeddrukDiastolischSchema,
    bloeddrukSystolischSchema,
    buikomvangSchema,
    gewichtInKgSchema,
    lengteInCmSchema,
    polsInBpmSchema,
} from '@common/validation/schemas/numberSchemas';
import { getTextAreaConditionalSchema } from '@common/validation/utils/getTextAreaConditionalSchema';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';

export type Intoxicaties = {
    alcohol: boolean | null;
    drugs: boolean | null;
    roken: boolean | null;
};

export type IntoxicatiesToelichting = {
    toelichting_alcohol: string | null;
    toelichting_drugs: string | null;
    toelichting_roken: string | null;
};

export type FamiliaireBelasting = {
    dm: boolean | null;
    hartfalen: boolean | null;
    hvz: boolean | null;
    hypertensie: boolean | null;
    overgewicht: boolean | null;
};

export type FamiliaireBelastingToelichting = {
    toelichting_dm: string | null;
    toelichting_hartfalen: string | null;
    toelichting_hvz: string | null;
    toelichting_hypertensie: string | null;
    toelichting_overgewicht: string | null;
};

export type AnderOnderzoek = {
    ander_onderzoek: boolean;
    cardiologisch_onderzoek: boolean;
    longfunctieonderzoek: boolean;
    onderzoek_interne_geneeskunde: boolean;
    slaaponderzoek: boolean;
};

export type AnderOnderzoekToelichting = {
    toelichting_ander_onderzoek: string | null;
    toelichting_cardiologisch_onderzoek: string | null;
    toelichting_longfunctieonderzoek: string | null;
    toelichting_onderzoek_interne_geneeskunde: string | null;
    toelichting_slaaponderzoek: string | null;
};

export type Medicatie = {
    medicatie: boolean | null;
};

export type MedicatieToelichting = {
    toelichting_medicatie: string | null;
};

export type BariPreMedConsultReportData = AnderOnderzoek &
    AnderOnderzoekToelichting &
    FamiliaireBelasting &
    FamiliaireBelastingToelichting &
    Intoxicaties &
    IntoxicatiesToelichting &
    Medicatie &
    MedicatieToelichting & {
        abdominaal: string | null;
        bloeddruk_diastolisch: number | null;
        bloeddruk_systolisch: number | null;
        buikomvang: number | null;
        extremiteiten: string | null;
        gewicht_in_kg: number | null;
        hart: string | null;
        lengte_in_cm: number | null;
        littekens: boolean | null;
        medische_voorgeschiedenis: string | null;
        pols: number | null;
        pulmonaal: string | null;
        souffles: boolean | null;
        speciele_anamnese: string | null;
        vag: boolean | null;
    };

export type BariPreMedConsultReportReadKeyReadData = BariPreMedConsultReportData & {
    aanvullende_onderzoeken: string;
    gewicht_lengte_bmi: string[];
};
export type BariPreMedConsultReportReadKey = keyof BariPreMedConsultReportReadKeyReadData;
export type BariPreMedConsultReportReadLabel =
    | 'Aanvullende onderzoeken'
    | 'Abdominaal'
    | 'Alcohol'
    | 'Bloeddruk diastolisch'
    | 'Bloeddruk systolisch'
    | 'Buikomvang'
    | 'DM'
    | 'Drugs'
    | 'Extremiteiten'
    | 'Gewicht / Lengte / BMI'
    | 'Hart'
    | 'Hartfalen'
    | 'HVZ'
    | 'Hypertensie'
    | 'Littekens'
    | 'Medicatie'
    | 'Medische voorgeschiedenis'
    | 'Overgewicht'
    | 'Pols'
    | 'Pulmonaal'
    | 'Roken'
    | 'Souffles'
    | 'Speciële anamnese'
    | 'Toelichting alcohol'
    | 'Toelichting ander onderzoek'
    | 'Toelichting cardiologisch onderzoek'
    | 'Toelichting DM'
    | 'Toelichting drugs'
    | 'Toelichting hartfalen'
    | 'Toelichting HVZ'
    | 'Toelichting hypertensie'
    | 'Toelichting longfunctieonderzoek'
    | 'Toelichting medicatie'
    | 'Toelichting onderzoek interne geneeskunde'
    | 'Toelichting overgewicht'
    | 'Toelichting roken'
    | 'Toelichting slaaponderzoek'
    | 'VAG';

export type BariPreMedConsultReportReadSectionTitle =
    | 'Aanvullende onderzoeken'
    | 'Anamnese'
    | 'Biometrische gegevens'
    | 'Familiaire belasting'
    | 'Intoxicaties'
    | 'Lichamelijk onderzoek'
    | 'Medicatie';

export const bariPreMedConsultReportReadSchema: TypedReadSchema<
    BariPreMedConsultReportReadKey,
    BariPreMedConsultReportReadLabel,
    BariPreMedConsultReportReadSectionTitle
>[] = [
    {
        items: [
            { key: 'medische_voorgeschiedenis', label: 'Medische voorgeschiedenis' },
            { key: 'speciele_anamnese', label: 'Speciële anamnese' },
        ],
        sectionTitle: 'Anamnese',
    },
    {
        items: [
            { key: 'alcohol', label: 'Alcohol', type: 'boolean' },
            { key: 'toelichting_alcohol', label: 'Toelichting alcohol' },

            { key: 'drugs', label: 'Drugs', type: 'boolean' },
            { key: 'toelichting_drugs', label: 'Toelichting drugs' },

            { key: 'roken', label: 'Roken', type: 'boolean' },
            { key: 'toelichting_roken', label: 'Toelichting roken' },
        ],
        sectionTitle: 'Intoxicaties',
    },
    {
        items: [
            { key: 'medicatie', label: 'Medicatie', type: 'boolean' },
            { key: 'toelichting_medicatie', label: 'Toelichting medicatie' },
        ],
        sectionTitle: 'Medicatie',
    },
    {
        items: [
            { key: 'overgewicht', label: 'Overgewicht', type: 'boolean' },
            { key: 'toelichting_overgewicht', label: 'Toelichting overgewicht' },

            { key: 'hypertensie', label: 'Hypertensie', type: 'boolean' },
            { key: 'toelichting_hypertensie', label: 'Toelichting hypertensie' },

            { key: 'hvz', label: 'HVZ', type: 'boolean' },
            { key: 'toelichting_hvz', label: 'Toelichting HVZ' },

            { key: 'hartfalen', label: 'Hartfalen', type: 'boolean' },
            { key: 'toelichting_hartfalen', label: 'Toelichting hartfalen' },

            { key: 'dm', label: 'DM', type: 'boolean' },
            { key: 'toelichting_dm', label: 'Toelichting DM' },
        ],
        sectionTitle: 'Familiaire belasting',
    },
    {
        items: [
            { key: 'gewicht_lengte_bmi', label: 'Gewicht / Lengte / BMI' },
            { append: 'mmHg', key: 'bloeddruk_systolisch', label: 'Bloeddruk systolisch', type: 'number' },
            { append: 'mmHg', key: 'bloeddruk_diastolisch', label: 'Bloeddruk diastolisch', type: 'number' },
            { append: 'cm', key: 'buikomvang', label: 'Buikomvang', type: 'number' },
            { append: '/min', key: 'pols', label: 'Pols', type: 'number' },
        ],
        sectionTitle: 'Biometrische gegevens',
    },
    {
        items: [
            { key: 'hart', label: 'Hart' },
            { key: 'souffles', label: 'Souffles', type: 'boolean' },
            { key: 'pulmonaal', label: 'Pulmonaal' },
            { key: 'vag', label: 'VAG', type: 'boolean' },
            { key: 'abdominaal', label: 'Abdominaal' },
            { key: 'littekens', label: 'Littekens', type: 'boolean' },
            { key: 'extremiteiten', label: 'Extremiteiten' },
        ],
        sectionTitle: 'Lichamelijk onderzoek',
    },
    {
        items: [
            { key: 'aanvullende_onderzoeken', label: 'Aanvullende onderzoeken' },
            { key: 'toelichting_cardiologisch_onderzoek', label: 'Toelichting cardiologisch onderzoek' },
            { key: 'toelichting_onderzoek_interne_geneeskunde', label: 'Toelichting onderzoek interne geneeskunde' },
            { key: 'toelichting_slaaponderzoek', label: 'Toelichting slaaponderzoek' },
            { key: 'toelichting_longfunctieonderzoek', label: 'Toelichting longfunctieonderzoek' },
            { key: 'toelichting_ander_onderzoek', label: 'Toelichting ander onderzoek' },
        ],
        sectionTitle: 'Aanvullende onderzoeken',
    },
];

const bariPreMedConsultReportFinal: TypedSchema<BariPreMedConsultReportData> = {
    abdominaal: getTextAreaStringSchema(),
    alcohol: Koi.boolean(),
    ander_onderzoek: Koi.boolean(),
    bloeddruk_diastolisch: bloeddrukDiastolischSchema,
    bloeddruk_systolisch: bloeddrukSystolischSchema,
    buikomvang: buikomvangSchema,
    cardiologisch_onderzoek: Koi.boolean(),
    dm: Koi.boolean(),
    drugs: Koi.boolean(),
    extremiteiten: getTextAreaStringSchema(),
    gewicht_in_kg: gewichtInKgSchema,
    hart: getTextAreaStringSchema(),
    hartfalen: Koi.boolean(),
    hvz: Koi.boolean(),
    hypertensie: Koi.boolean(),
    lengte_in_cm: lengteInCmSchema,
    littekens: Koi.boolean(),
    longfunctieonderzoek: Koi.boolean(),
    medicatie: Koi.boolean(),
    medische_voorgeschiedenis: getTextAreaStringSchema(),
    onderzoek_interne_geneeskunde: Koi.boolean(),
    overgewicht: Koi.boolean(),
    pols: polsInBpmSchema,
    pulmonaal: getTextAreaStringSchema(),
    roken: Koi.boolean(),
    slaaponderzoek: Koi.boolean(),
    souffles: Koi.boolean(),
    speciele_anamnese: getTextAreaStringSchema(),
    toelichting_alcohol: getTextAreaConditionalSchema('alcohol'),
    toelichting_ander_onderzoek: getTextAreaConditionalSchema('ander_onderzoek'),
    toelichting_cardiologisch_onderzoek: getTextAreaConditionalSchema('cardiologisch_onderzoek'),
    toelichting_dm: getTextAreaConditionalSchema('dm'),
    toelichting_drugs: getTextAreaConditionalSchema('drugs'),
    toelichting_hartfalen: getTextAreaConditionalSchema('hartfalen'),
    toelichting_hvz: getTextAreaConditionalSchema('hvz'),
    toelichting_hypertensie: getTextAreaConditionalSchema('hypertensie'),
    toelichting_longfunctieonderzoek: getTextAreaConditionalSchema('longfunctieonderzoek'),
    toelichting_medicatie: getTextAreaConditionalSchema('medicatie'),
    toelichting_onderzoek_interne_geneeskunde: getTextAreaConditionalSchema('onderzoek_interne_geneeskunde'),
    toelichting_overgewicht: getTextAreaConditionalSchema('overgewicht'),
    toelichting_roken: getTextAreaConditionalSchema('roken'),
    toelichting_slaaponderzoek: getTextAreaConditionalSchema('slaaponderzoek'),
    vag: Koi.boolean(),
};

export const bariPreMedConsultReportFinalSchema = Koi.object(bariPreMedConsultReportFinal);
